import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Lock, ExternalLink, TrendingUp, Timer, DollarSign, RefreshCw, ShoppingBag } from "lucide-react";
import { apiEbayComps, getSessionId } from "@/lib/api";
import { money } from "@/lib/utils";

const PAID_TIERS = new Set(["reseller", "pro"]);

export default function EbaySoldComps({ report, plan, onUpgrade, onAnalytics }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!report) return;
    let alive = true;
    setLoading(true);
    setError(null);
    apiEbayComps(getSessionId(), report)
      .then((r) => {
        if (alive) {
          setData(r);
          if (onAnalytics) onAnalytics(r.analytics || null);
        }
      })
      .catch((e) => { if (alive) setError(e?.message || "Failed to load comps"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [report, onAnalytics]);

  const isPaid = PAID_TIERS.has((plan || "free").toLowerCase());
  const items = data?.items || [];
  const analytics = data?.analytics || {};
  const source = data?.source || "unavailable";
  const broadMatch = data?.broad_match;
  const labels = data?.labels || {
    eyebrow: "Market feed unavailable",
    headline: "Market Comps",
    avg_stat_label: "Avg",
    kind: "unavailable",
    footnote: "",
  };
  const isAuthoritative = labels.kind === "sold";

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass p-4 sm:p-6 space-y-5"
      data-testid="ebay-comps-card"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-[#0F172A] flex items-center justify-center">
            <ShoppingBag size={14} className="text-white" />
          </div>
          <div>
            <h3 className="text-[#0F172A] font-semibold text-sm leading-tight">{labels.headline}</h3>
            <div className="text-[10px] uppercase tracking-[0.15em] text-[#94A3B8] font-semibold mt-0.5">
              {labels.eyebrow}
            </div>
          </div>
        </div>
        {loading && <RefreshCw size={14} className="text-[#94A3B8] animate-spin" data-testid="ebay-comps-loading" />}
      </div>

      {/* Compact analytics row — single line, no boxes */}
      {(analytics.avg_sold_price !== null || items.length > 0) && (
        <div className="flex flex-wrap items-center gap-x-6 gap-y-1.5 text-xs" data-testid="ebay-comps-analytics">
          <div className="flex items-baseline gap-1.5">
            <span className="text-[10px] uppercase tracking-wider text-white/50 font-semibold">{labels.avg_stat_label}</span>
            <span className="text-[#0F172A] font-mono-num text-sm font-bold">
              {analytics.avg_sold_price != null ? money(analytics.avg_sold_price) : "—"}
            </span>
          </div>
          {analytics.price_range?.low != null && (
            <div className="flex items-baseline gap-1.5">
              <span className="text-[10px] uppercase tracking-wider text-white/50 font-semibold">Range</span>
              <span className="text-[#334155] font-mono-num text-sm">
                {money(analytics.price_range.low)}–{money(analytics.price_range.high)}
              </span>
            </div>
          )}
          {analytics.sell_velocity_label && (
            <div className="flex items-center gap-1.5">
              <Timer size={11} className="text-[#10B981]"/>
              <span className="text-[#10B981] text-xs font-medium">{analytics.sell_velocity_label}</span>
            </div>
          )}
        </div>
      )}

      {broadMatch && (
        <div className="text-[11px] uppercase tracking-[0.15em] text-[#B45309] font-medium bg-[#FEF3C7] border border-[#F59E0B]/40 rounded-lg px-3 py-2">
          Broad search match — style code stripped for better results
        </div>
      )}

      {labels.footnote && items.length > 0 && (
        <div
          className={`text-[11px] rounded-lg px-3 py-2 border ${
            isAuthoritative
              ? "bg-[#10B981]/10 border-[#10B981]/30 text-[#10B981]"
              : "bg-white/[0.03] border-white/10 text-white/60"
          }`}
          data-testid="ebay-comps-footnote"
        >
          {labels.footnote}
        </div>
      )}

      {/* Empty / unavailable state */}
      {!loading && items.length === 0 && (
        <div className="rounded-xl bg-white/[0.03] border border-white/10 p-5 text-center" data-testid="ebay-comps-empty">
          <div className="text-white/70 text-sm mb-1">
            {source === "unavailable" ? "Live market feed unavailable" : "No comps for this exact item"}
          </div>
          <div className="text-white/40 text-xs">
            {labels.footnote || "Try broadening your search or adjust the brand lock."}
          </div>
        </div>
      )}

      {/* Comp cards — tighter grid */}
      {items.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-10 gap-2" data-testid="ebay-comps-grid">
          {items.slice(0, 10).map((it, idx) => (
            <CompCard key={it.id} item={it} unlocked={!it.isLocked} onUpgrade={onUpgrade} idx={idx} />
          ))}
        </div>
      )}

      {/* Free tier CTA */}
      {!isPaid && items.length > 1 && (
        <button
          data-testid="ebay-comps-upgrade-cta"
          onClick={onUpgrade}
          className="w-full py-2.5 min-h-[44px] rounded-full bg-[#0F172A] text-white font-semibold text-xs hover:bg-[#1E293B] transition-colors flex items-center justify-center gap-2"
        >
          <Lock size={12} /> Unlock {Math.max(0, (data?.total_available || items.length) - 1)}+ more comps · full history · velocity
        </button>
      )}

      {error && <div className="text-[#F59E0B] text-xs">{error}</div>}
    </motion.div>
  );
}

function CompCard({ item, unlocked, onUpgrade, idx }) {
  if (!unlocked) {
    return (
      <button
        data-testid={`ebay-comp-locked-${idx}`}
        onClick={onUpgrade}
        aria-label="Locked comp — upgrade to view"
        className="relative rounded-ds-sm overflow-hidden aspect-square bg-surface-sunken border border-border-default group hover:border-ink-900 transition-colors"
      >
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-1">
          <Lock size={16} className="text-lock-fill group-hover:text-ink-700 transition-colors" />
          <div className="text-[9px] text-lock-fill group-hover:text-ink-700 font-semibold transition-colors">Unlock</div>
        </div>
      </button>
    );
  }

  const total = (item.price || 0) + (item.shipping || 0);
  return (
    <a
      data-testid={`ebay-comp-unlocked-${idx}`}
      href={item.link || "#"}
      target="_blank"
      rel="noopener noreferrer"
      className="relative rounded-lg overflow-hidden aspect-square bg-[#F1F5F9] border border-[#E9ECEF] flex flex-col hover:border-[#0F172A] transition-colors group"
      title={`${item.title} — ${money(item.price)}${item.shipping ? ` +${money(item.shipping)} ship` : ""} · ${item.sold_text || ""}`}
    >
      {item.image ? (
        <img src={item.image} alt="" className="absolute inset-0 w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center text-[#94A3B8]"><ShoppingBag size={18} /></div>
      )}
      <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 to-transparent p-1.5">
        <div className="text-white text-[11px] font-bold font-mono-num leading-none">{money(item.price)}</div>
      </div>
      <ExternalLink size={9} className="absolute top-1 right-1 text-white bg-black/50 rounded p-0.5" />
    </a>
  );
}
